import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Lock, Mail, ArrowLeft, Shield, Zap, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { login } from '../../store/slices/authSlice';
import authService from '../../services/auth/authService';

type UserRole = 'superadmin' | 'admin';

const AdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState<UserRole>('admin');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Call appropriate login API based on selected role
      let token: string;
      
      if (selectedRole === 'superadmin') {
        token = await authService.superAdminLogin({
          email: formData.email,
          password: formData.password
        });
      } else {
        token = await authService.adminLogin({
          email: formData.email,
          password: formData.password
        });
      }
      
      // Dispatch to Redux store
      dispatch(login({
        token,
        user: {
          email: formData.email,
          role: selectedRole,
          name: formData.email.split('@')[0]
        }
      }));

      // Show success message
      toast.success(`Welcome back! Logged in as ${selectedRole === 'superadmin' ? 'Super Admin' : 'Admin'}`);
      
      // Redirect immediately after successful login - no setTimeout needed
      // The token is already set in localStorage by authService
      if (selectedRole === 'superadmin') {
        navigate('/super-admin/dashboard', { replace: true });
      } else {
        navigate('/admin/dashboard', { replace: true });
      }
      
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Invalid credentials. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-secondary-50 flex items-center justify-center px-4 py-8">
      {/* Back to Home */}
      <Link
        to="/"
        className="fixed top-6 left-6 text-secondary-600 hover:text-primary-500 flex items-center space-x-2 transition-colors font-semibold"
      >
        <ArrowLeft size={20} />
        <span>Back</span>
      </Link>

      <div className="w-full max-w-md">
        {/* Logo & Header */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="flex items-center justify-center mb-4">
            <img 
              src="/rita_logo_2-removebg-preview.png" 
              alt="Rita Logo" 
              className="h-28 w-auto object-contain mx-auto"
            />
          </div>
          <div className="mb-2">
            <p className="text-sm text-secondary-600">Admin Portal</p>
          </div>
          <h2 className="text-3xl font-bold text-secondary-900 mb-2">Welcome Back</h2>
          <p className="text-secondary-600">Sign in to your account</p>
        </div>

        {/* Login Form Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8 animate-slide-up">
          {error && (
            <div className="bg-danger-50 border border-danger-500 text-danger-600 px-4 py-3 rounded-lg mb-6 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Role Selection */}
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-3">
                Select Role
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setSelectedRole('superadmin')}
                  className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-semibold transition-all ${
                    selectedRole === 'superadmin'
                      ? 'bg-primary-500 text-white border-2 border-primary-600 shadow-green'
                      : 'bg-white text-secondary-600 border-2 border-secondary-200 hover:border-primary-300'
                  }`}
                >
                  <Shield size={20} />
                  <span>Super Admin</span>
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedRole('admin')}
                  className={`flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-semibold transition-all ${
                    selectedRole === 'admin'
                      ? 'bg-primary-500 text-white border-2 border-primary-600 shadow-green'
                      : 'bg-white text-secondary-600 border-2 border-secondary-200 hover:border-primary-300'
                  }`}
                >
                  <Zap size={20} />
                  <span>Admin</span>
                </button>
              </div>
            </div>

            {/* Email Field */}
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" size={20} />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full pl-11 pr-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none transition-colors"
                  placeholder={selectedRole === 'superadmin' ? 'superadmin@rita.com' : 'admin@rita.com'}
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-semibold text-secondary-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full pl-11 pr-12 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none transition-colors"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Forgot Password */}
            <div className="flex justify-end">
              <Link to="/admin/forgot-password" className="text-sm text-primary-600 hover:text-primary-700 font-semibold">
                Forgot Password?
              </Link>
            </div>

            {/* Sign In Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-500 hover:bg-primary-600 text-white py-3.5 rounded-lg font-semibold transition-all hover:scale-[1.02] shadow-green disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              <span>{loading ? 'Signing in...' : 'Sign In'}</span>
              {!loading && <ArrowRight size={20} />}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-secondary-500 text-sm mt-6">
          © 2025 Rita. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;

